<template>
	<div class="tournament-module">
		<div class="card border mb-3">
			<div class="card-body py-2">
				<div class="row align-items-end">
					<div class="col-md-8 col-sm-12">
						<div class="form-group mb-0">
							<label for="tournament_event_select" class="small text-muted mb-1">Event (this
								session)</label>
							<select id="tournament_event_select" class="form-control form-control-sm"
								:class="{ 'is-invalid': errors.tournament_event_id }"
								v-model="selectedTournamentEventId">
								<option value="">Select event *</option>
								<option v-for="ev in tournamentEvents" :key="ev.tournament_event_id"
									:value="String(ev.tournament_event_id)">
									{{ eventOptionLabel(ev) }}
								</option>
							</select>
							<span v-if="errors.tournament_event_id" class="invalid-feedback d-block">{{
								errors.tournament_event_id[0]
							}}</span>
						</div>
					</div>
					<div class="col-md-4 col-sm-12 mt-2 mt-md-0 text-md-right">
						<button v-can="'tournament_events.update'" type="button"
							class="btn btn-sm btn-outline-secondary mr-2" data-toggle="modal"
							data-target="#tournamentEventModal" :disabled="!selectedEvent" @click="openEditEventModal">
							<i class="fas fa-edit"></i> Edit event
						</button>
						<button v-can="'tournament_events.create'" type="button" class="btn btn-sm btn-outline-primary"
							data-toggle="modal" data-target="#tournamentEventModal" @click="openCreateEventModal">
							<i class="fas fa-plus"></i> Add event
						</button>
					</div>
				</div>
			</div>
		</div>

		<form @submit.prevent="addSession">
			<div class="card-body">
				<div class="row mt-2">
					<div class="col-sm-6">
						<div class="form-group">
							<label for="customer_id">Customer *</label>
							<Customer :checkIn="true" :class="{ 'is-invalid': errors.customer_id }"
								:customClass="{ 'is-invalid': errors.customer_id }" :initialize="session.customer_name"
								@selectCustomer="selectCustomer($event)" ref="customer_id"></Customer>

							<span v-if="errors.customer_id" class="invalid-feedback">{{
								errors.customer_id[0]
							}}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="datetime">Date &amp; Time *</label>
							<input type="datetime-local" class="form-control form-control-sm"
								:class="{ 'is-invalid': errors.session_date || errors.session_time }" step="60"
								v-model="datetime" ref="datetime" />
							<span v-if="errors.session_date" class="invalid-feedback">{{ errors.session_date[0]
							}}</span>
							<span v-else-if="errors.session_time" class="invalid-feedback">{{ errors.session_time[0]
							}}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="amount">Amount *</label>
							<input type="number" class="form-control form-control-sm"
								:class="{ 'is-invalid': errors.amount }"
								:placeholder="selectedEvent ? 'Event total: ' + formatMoney(selectedEvent.total_amount) : 'Amount'"
								v-model="session.amount" ref="amount" step="0.01" disabled />
							<span v-if="errors.amount" class="invalid-feedback">{{ errors.amount[0] }}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="table_id">Table No *</label>
							<select class="form-control form-control-sm" :class="{ 'is-invalid': errors.table_id }"
								v-model="session.table_id" ref="table_id">
								<option value="">Select table</option>
								<option v-for="(table, key) in tables" :key="key" :value="table.table_id">{{ table.table
								}}
								</option>
							</select>
							<span v-if="errors.table_id" class="invalid-feedback">{{ errors.table_id[0] }}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="seat_no">Seat No *</label>
							<select class="form-control form-control-sm" :class="{ 'is-invalid': errors.seat_no }"
								v-model="session.seat_no" ref="seat_no">
								<option value="">{{ availableSeats.length > 0 ? "Select seat" : "No available seat" }}
								</option>
								<option v-for="seat in availableSeats" :key="seat" :value="seat">Seat {{ seat }}
								</option>
							</select>
							<span v-if="errors.seat_no" class="invalid-feedback">{{ errors.seat_no[0] }}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="payment_mode_id">Payment mode *</label>
							<select class="form-control form-control-sm"
								:class="{ 'is-invalid': errors.payment_mode_id }" ref="payment_mode_id"
								v-model="session.payment_mode_id">
								<option value="">Select payment mode</option>
								<option v-for="(payment_mode, key) in payment_modes" :key="key"
									:value="payment_mode.payment_mode_id">
									{{ payment_mode.payment_mode }}
								</option>
							</select>
							<span v-if="errors.payment_mode_id" class="invalid-feedback">{{ errors.payment_mode_id[0]
							}}</span>
						</div>
					</div>
					<div class="col-sm-3">
						<div class="form-group">
							<label for="reference_no">Reference No</label>
							<input type="text" class="form-control form-control-sm"
								:class="{ 'is-invalid': errors.reference_no }" placeholder="Reference no"
								v-model="session.reference_no" />
							<span v-if="errors.reference_no" class="invalid-feedback">{{ errors.reference_no[0]
							}}</span>
						</div>
					</div>
					<div class="col-sm-12">
						<div class="form-group">
							<label for="note">Note</label>
							<textarea class="form-control form-control-sm" :class="{ 'is-invalid': errors.note }"
								placeholder="Note" v-model="session.note"></textarea>
							<span v-if="errors.note" class="invalid-feedback">{{ errors.note[0] }}</span>
						</div>
					</div>
				</div>
			</div>
			<div class="card-footer">
				<div class="float-right">
					<button class="btn btn-sm btn-outline-success" type="submit">
						<i class="fas fa-save"></i> Submit
					</button>
				</div>
			</div>
		</form>

		<div class="modal fade" id="tournamentEventModal" tabindex="-1" role="dialog" aria-hidden="true">
			<div class="modal-dialog modal-lg" role="document">
				<div class="modal-content">
					<form @submit.prevent="saveTournamentEvent">
						<div class="modal-header">
							<h5 class="modal-title">{{ eventFormMode === "edit" ? "Edit tournament event" : "Create tournament event" }}</h5>
							<button type="button" class="close" data-dismiss="modal" aria-label="Close">
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div class="modal-body">
							<div class="row">
								<div class="col-md-6">
									<div class="form-group">
										<label>Title *</label>
										<input type="text" class="form-control form-control-sm"
											:class="{ 'is-invalid': eventFormErrors.title }"
											v-model="eventForm.title" />
										<span v-if="eventFormErrors.title" class="invalid-feedback d-block">{{
											eventFormErrors.title[0] }}</span>
									</div>
								</div>
								<div class="col-md-3">
									<div class="form-group">
										<label>Start date *</label>
										<input type="date" class="form-control form-control-sm"
											:class="{ 'is-invalid': eventFormErrors.start_date }"
											v-model="eventForm.start_date" />
										<span v-if="eventFormErrors.start_date" class="invalid-feedback d-block">{{
											eventFormErrors.start_date[0] }}</span>
									</div>
								</div>
								<div class="col-md-3">
									<div class="form-group">
										<label>Start time *</label>
										<input type="time" class="form-control form-control-sm" step="60"
											:class="{ 'is-invalid': eventFormErrors.start_time }"
											v-model="eventForm.start_time" />
										<span v-if="eventFormErrors.start_time" class="invalid-feedback d-block">{{
											eventFormErrors.start_time[0] }}</span>
									</div>
								</div>
								<div class="col-md-12">
									<div class="form-group">
										<label>Description</label>
										<textarea class="form-control form-control-sm"
											:class="{ 'is-invalid': eventFormErrors.description }" rows="2"
											v-model="eventForm.description"></textarea>
										<span v-if="eventFormErrors.description" class="invalid-feedback d-block">{{
											eventFormErrors.description[0] }}</span>
									</div>
								</div>
								<div class="col-md-4">
									<div class="form-group">
										<label>Amount *</label>
										<input type="number" class="form-control form-control-sm" step="0.01" min="0"
											:class="{ 'is-invalid': eventFormErrors.amount }" v-model="eventForm.amount"
											@input="syncEventTotal" />
										<span v-if="eventFormErrors.amount" class="invalid-feedback d-block">{{
											eventFormErrors.amount[0] }}</span>
									</div>
								</div>
								<div class="col-md-4">
									<div class="form-group">
										<label>House fee *</label>
										<input type="number" class="form-control form-control-sm" step="0.01" min="0"
											:class="{ 'is-invalid': eventFormErrors.house_fee }"
											v-model="eventForm.house_fee" @input="syncEventTotal" />
										<span v-if="eventFormErrors.house_fee" class="invalid-feedback d-block">{{
											eventFormErrors.house_fee[0] }}</span>
									</div>
								</div>
								<div class="col-md-4">
									<div class="form-group">
										<label>Total amount *</label>
										<input type="number" class="form-control form-control-sm" step="0.01" min="0"
											:class="{ 'is-invalid': eventFormErrors.total_amount }"
											v-model="eventForm.total_amount" />
										<span v-if="eventFormErrors.total_amount" class="invalid-feedback d-block">{{
											eventFormErrors.total_amount[0] }}</span>
									</div>
								</div>
								<div class="col-md-12">
									<label class="d-block">Data fields</label>
									<div v-for="(field, idx) in eventForm.data_fields" :key="'df-' + idx"
										class="input-group input-group-sm mb-2">
										<input type="text" class="form-control" v-model="eventForm.data_fields[idx]"
											placeholder="Custom field text" />
										<div class="input-group-append">
											<button type="button" class="btn btn-outline-secondary"
												@click="removeDataField(idx)"
												:disabled="eventForm.data_fields.length <= 1">
												<i class="fas fa-times"></i>
											</button>
										</div>
									</div>
									<button type="button" class="btn btn-sm btn-outline-secondary"
										@click="addDataField">
										<i class="fas fa-plus"></i> Add data field
									</button>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-sm btn-secondary" data-dismiss="modal">Cancel</button>
							<button v-if="eventFormMode === 'create'" v-can="'tournament_events.create'" type="submit"
								class="btn btn-sm btn-success">
								<i class="fas fa-save"></i> Save event
							</button>
							<button v-else v-can="'tournament_events.update'" type="submit"
								class="btn btn-sm btn-success">
								<i class="fas fa-save"></i> {{ eventFormMode === "edit" ? "Update event" : "Save event"
								}}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
let Customer = require("@/components/Customer.vue").default;
import moment from "moment";
export default {
	name: "Session.Tournament",
	components: { Customer },
	props: {
		session_log_id: {
			default: null,
		},
	},
	data() {
		return {
			datetime: "",
			session: {
				branch_id: this.$store.getters.user.branch_id,
				user_id: this.$store.getters.user.user_id,
				session_log_id: "",
				session_date: "",
				session_time: "",
				session_type: "Tournament",
				customer_id: "",
				customer_name: "",
				customer_no: "",
				customer_real_name: "",
				tournament_event_id: "",
				table_id: "",
				seat_no: "",
				amount: "",
				payment_mode_id: "",
				reference_no: "",
				note: "",
			},
			tables: [],
			availableSeats: [],
			payment_modes: [],
			errors: [],
			tournamentEvents: [],
			selectedTournamentEventId: "",
			eventFormErrors: {},
			eventFormMode: "create",
			editingEventId: "",
			eventForm: {
				title: "",
				description: "",
				start_date: "",
				start_time: "",
				amount: "",
				house_fee: "",
				total_amount: "",
				data_fields: [""],
			},
		};
	},
	computed: {
		selectedEvent() {
			const id = String(this.selectedTournamentEventId || "");
			if (!id) {
				return null;
			}
			return (
				this.tournamentEvents.find(
					(e) => String(e.tournament_event_id) === id
				) || null
			);
		},
	},
	watch: {
		session_log_id(val) {
			this.session.session_log_id = val || "";
			if (!val) {
				this.session.table_id = "";
				this.session.seat_no = "";
				this.tournamentEvents = [];
				this.selectedTournamentEventId = "";
				return;
			}
			this.discard();
		},
		selectedTournamentEventId() {
			this.applyAmountFromSelectedEvent();
			this.getTables();
		},
		"session.table_id"() {
			this.loadAvailableSeatsForSelectedTable();
		},
	},
	mounted() {
		let vm = this;
		vm.session.session_log_id = vm.session_log_id || "";
		vm.datetime = moment().format("YYYY-MM-DDTHH:mm");
		vm.syncDateTimeFromInput();
		vm.getPaymentModes();
		if (vm.session.session_log_id) {
			vm.loadTournamentEvents();
		}
		vm.$nextTick(() => {
			if (vm.$refs.customer_id) {
				vm.$refs.customer_id.focus();
			}
		});
	},
	methods: {
		getPaymentModes() {
			let vm = this;
			if (vm.$store.getters.payment_modes.length > 0) {
				vm.payment_modes = vm.$store.getters.payment_modes;
				return;
			}
			vm.$store
				.dispatch("post", { uri: "paymentModes" })
				.then(function (response) {
					vm.payment_modes = response.data.data || [];
					vm.$store.dispatch("setPaymentModes", vm.payment_modes);
				})
				.catch(function (error) {
					vm.errors = error.response.data;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},

		formatMoney(val) {
			if (val === "" || val == null) {
				return "";
			}
			const n = Number(val);
			if (!Number.isFinite(n)) {
				return String(val);
			}
			return n.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},

		eventOptionLabel(ev) {
			const title = ev.title || "Event";
			const dateStr = ev.start_date ? String(ev.start_date).slice(0, 10) : "";
			let timeStr = (ev.start_time || "").toString();
			if (timeStr.length >= 8) {
				timeStr = timeStr.slice(0, 8);
			} else if (timeStr.length === 5) {
				timeStr = timeStr + ":00";
			}
			let when = "";
			if (dateStr) {
				const combined = timeStr ? `${dateStr} ${timeStr}` : dateStr;
				const m = moment(
					combined,
					["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", moment.ISO_8601],
					true
				);
				if (m.isValid()) {
					when = m.format("DD MMM YYYY, HH:mm");
				} else {
					const m2 = moment(dateStr, "YYYY-MM-DD", true);
					when = m2.isValid()
						? m2.format("DD MMM YYYY") + (timeStr ? ", " + timeStr.slice(0, 5) : "")
						: [dateStr, timeStr].filter(Boolean).join(" ");
				}
			}
			return when ? `${title} — ${when}` : title;
		},

		applyAmountFromSelectedEvent() {
			const ev = this.selectedEvent;
			if (!ev) {
				if (!this.selectedTournamentEventId) {
					this.session.tournament_event_id = "";
					this.session.amount = "";
					this.session.table_id = "";
					this.session.seat_no = "";
					this.availableSeats = [];
				}
				return;
			}
			this.session.tournament_event_id = String(ev.tournament_event_id || "");
			if (ev.total_amount === "" || ev.total_amount == null) {
				return;
			}
			const num = Number(ev.total_amount);
			if (!Number.isFinite(num)) {
				return;
			}
			this.session.amount = num.toFixed(2);
		},

		/**
		 * Keep prior selection when still in list; otherwise default to first row in the list.
		 * After create, pass { selectEventId } to select the new row.
		 */
		syncEventDropdownAfterLoad(opts) {
			opts = opts || {};
			const list = this.tournamentEvents || [];
			const preferred =
				opts.selectEventId != null && opts.selectEventId !== ""
					? String(opts.selectEventId)
					: null;
			let next = "";
			if (
				preferred &&
				list.some((e) => String(e.tournament_event_id) === preferred)
			) {
				next = preferred;
			} else {
				const prev = String(this.selectedTournamentEventId || "");
				const prevStillThere =
					prev && list.some((e) => String(e.tournament_event_id) === prev);
				if (prevStillThere) {
					next = prev;
				} else if (list.length > 0) {
					next = String(list[0].tournament_event_id);
				}
			}
			this.selectedTournamentEventId = next;
			this.applyAmountFromSelectedEvent();
		},

		loadTournamentEvents(opts) {
			let vm = this;
			if (!vm.session.session_log_id && !vm.session_log_id) {
				return;
			}
			const sid = vm.session.session_log_id || vm.session_log_id;
			vm.$store
				.dispatch("post", {
					uri: "getTournamentEvents",
					data: {
						session_log_id: sid,
						branch_id: vm.session.branch_id,
					},
				})
				.then(function (response) {
					vm.tournamentEvents = response.data.data || [];
					vm.syncEventDropdownAfterLoad(opts || {});
				})
				.catch(function () {
					vm.tournamentEvents = [];
					vm.syncEventDropdownAfterLoad({});
				});
		},

		resetEventForm() {
			this.eventFormErrors = {};
			this.eventFormMode = "create";
			this.editingEventId = "";
			this.eventForm = {
				title: "",
				description: "",
				start_date: moment().format("YYYY-MM-DD"),
				start_time: moment().format("HH:mm"),
				amount: "",
				house_fee: "",
				total_amount: "",
				data_fields: [""],
			};
			this.syncEventTotal();
		},

		openCreateEventModal() {
			this.resetEventForm();
		},

		openEditEventModal() {
			const ev = this.selectedEvent;
			if (!ev) {
				return;
			}
			this.eventFormErrors = {};
			this.eventFormMode = "edit";
			this.editingEventId = String(ev.tournament_event_id || "");
			this.eventForm = {
				title: ev.title || "",
				description: ev.description || "",
				start_date: ev.start_date ? String(ev.start_date).slice(0, 10) : "",
				start_time: (ev.start_time || "").toString().slice(0, 5),
				amount: ev.amount != null ? String(ev.amount) : "",
				house_fee: ev.house_fee != null ? String(ev.house_fee) : "",
				total_amount: ev.total_amount != null ? String(ev.total_amount) : "",
				data_fields: Array.isArray(ev.data_fields) && ev.data_fields.length > 0
					? ev.data_fields.map((f) => (f == null ? "" : String(f)))
					: [""],
			};
			this.syncEventTotal();
		},

		syncEventTotal() {
			const a = parseFloat(this.eventForm.amount) || 0;
			const h = parseFloat(this.eventForm.house_fee) || 0;
			this.eventForm.total_amount = (a + h).toFixed(2);
		},

		addDataField() {
			this.eventForm.data_fields.push("");
		},

		removeDataField(idx) {
			if (this.eventForm.data_fields.length <= 1) {
				this.eventForm.data_fields[0] = "";
				return;
			}
			this.eventForm.data_fields.splice(idx, 1);
		},

		hideEventModal() {
			if (typeof window.$ !== "undefined" && window.$("#tournamentEventModal").modal) {
				window.$("#tournamentEventModal").modal("hide");
			}
		},

		createTournamentEvent() {
			let vm = this;
			vm.syncEventTotal();
			const sid = vm.session.session_log_id || vm.session_log_id;
			const payload = {
				session_log_id: sid,
				branch_id: vm.session.branch_id,
				title: vm.eventForm.title,
				description: vm.eventForm.description || null,
				start_date: vm.eventForm.start_date,
				start_time: vm.eventForm.start_time,
				amount: vm.eventForm.amount,
				house_fee: vm.eventForm.house_fee,
				total_amount: vm.eventForm.total_amount,
				data_fields: vm.eventForm.data_fields.filter((s) => s != null && String(s).trim() !== ""),
			};
			const loader = vm.$loading.show();
			vm.eventFormErrors = {};
			vm.$store
				.dispatch("post", { uri: "createTournamentEvent", data: payload })
				.then(function (response) {
					loader.hide();
					vm.$store.dispatch("success", response.data.message || "Event created");
					vm.hideEventModal();
					const newId =
						response.data.data && response.data.data.tournament_event_id;
					vm.loadTournamentEvents(
						newId != null ? { selectEventId: newId } : {}
					);
				})
				.catch(function (error) {
					loader.hide();
					if (error.response && error.response.data && error.response.data.errors) {
						vm.eventFormErrors = error.response.data.errors;
					}
					vm.$store.dispatch("error", error.response?.data?.message || "Could not create event");
				});
		},

		updateTournamentEvent() {
			let vm = this;
			vm.syncEventTotal();
			const sid = vm.session.session_log_id || vm.session_log_id;
			const payload = {
				tournament_event_id: vm.editingEventId,
				session_log_id: sid,
				branch_id: vm.session.branch_id,
				title: vm.eventForm.title,
				description: vm.eventForm.description || null,
				start_date: vm.eventForm.start_date,
				start_time: vm.eventForm.start_time,
				amount: vm.eventForm.amount,
				house_fee: vm.eventForm.house_fee,
				total_amount: vm.eventForm.total_amount,
				data_fields: vm.eventForm.data_fields.filter((s) => s != null && String(s).trim() !== ""),
			};
			const loader = vm.$loading.show();
			vm.eventFormErrors = {};
			vm.$store
				.dispatch("post", { uri: "updateTournamentEvent", data: payload })
				.then(function (response) {
					loader.hide();
					vm.$store.dispatch("success", response.data.message || "Event updated");
					vm.hideEventModal();
					vm.loadTournamentEvents({ selectEventId: vm.editingEventId });
				})
				.catch(function (error) {
					loader.hide();
					if (error.response && error.response.data && error.response.data.errors) {
						vm.eventFormErrors = error.response.data.errors;
					}
					vm.$store.dispatch("error", error.response?.data?.message || "Could not update event");
				});
		},

		saveTournamentEvent() {
			if (this.eventFormMode === "edit") {
				this.updateTournamentEvent();
				return;
			}
			this.createTournamentEvent();
		},

		sortTablesById() {
			this.tables = [...this.tables].sort(
				(a, b) => Number(a.table_id) - Number(b.table_id)
			);
		},

		syncDateTimeFromInput() {
			let vm = this;
			if (!vm.datetime) {
				vm.session.session_date = "";
				vm.session.session_time = "";
				return;
			}
			const parts = vm.datetime.split("T");
			vm.session.session_date = parts[0] || "";
			let t = parts[1] || "00:00";
			if (t.length === 5) {
				t = t + ":00";
			}
			vm.session.session_time = t;
		},

		applyNextTournamentSlot() {
			let vm = this;
			if (!vm.session.session_log_id || !vm.selectedTournamentEventId) {
				return;
			}
			vm.$store
				.dispatch("post", {
					uri: "getNextTournamentSlot",
					data: {
						session_log_id: vm.session.session_log_id,
						branch_id: vm.session.branch_id,
						tournament_event_id: vm.selectedTournamentEventId,
					},
				})
				.then(function (response) {
					const d = response.data.data;
					if (!d) {
						return;
					}
					vm.session.table_id = d.table_id;
					let seat = Number(d.seat_no);
					if (Number.isFinite(seat)) {
						if (seat > 9) {
							seat = 9;
						}
						if (seat < 1) {
							seat = "";
						}
						vm.session.seat_no = seat === "" ? "" : seat;
					} else {
						vm.session.seat_no = d.seat_no;
					}
					vm.loadAvailableSeatsForSelectedTable();
				})
				.catch(function (error) {
					const msg =
						error.response &&
						error.response.data &&
						error.response.data.message;
					if (msg) {
						vm.$store.dispatch("error", msg);
					}
				});
		},

		getTables() {
			let vm = this;
			vm.session.session_log_id = vm.session_log_id || "";
			vm.session.tournament_event_id = vm.selectedTournamentEventId || "";
			const finish = function () {
				vm.sortTablesById();
				vm.discard();
			};
			let uri = { uri: "getBranchTables", data: vm.session };
			vm.$store
				.dispatch("post", uri)
				.then(function (response) {
					vm.tables = response.data.data;
					finish();
				})
				.catch(function (error) {
					vm.errors = error.response.data;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},

		loadAvailableSeatsForSelectedTable() {
			let vm = this;
			if (!vm.selectedTournamentEventId || !vm.session.table_id || !vm.session.session_log_id) {
				vm.availableSeats = [];
				if (!vm.session.table_id) {
					vm.session.seat_no = "";
				}
				return;
			}
			vm.$store
				.dispatch("post", {
					uri: "getTournamentTableSeatStatus",
					data: {
						branch_id: vm.session.branch_id,
						session_log_id: vm.session.session_log_id,
						tournament_event_id: vm.selectedTournamentEventId,
						table_id: vm.session.table_id,
					},
				})
				.then(function (response) {
					const seats = (response.data.data || [])
						.filter((s) => !s.is_running)
						.map((s) => Number(s.seat_no))
						.sort((a, b) => a - b);
					vm.availableSeats = seats;
					const current = Number(vm.session.seat_no);
					if (!seats.includes(current)) {
						vm.session.seat_no = seats.length > 0 ? seats[0] : "";
					}
				})
				.catch(function () {
					vm.availableSeats = [];
					vm.session.seat_no = "";
				});
		},

		selectCustomer(e) {
			let vm = this;
			const customer = e.target.value;
			vm.session.customer_id = customer.customer_id;
			vm.session.customer_no = customer.customer_no || "";
			vm.session.customer_real_name = customer.customer_name || "";
			vm.session.customer_name =
				customer.customer_no + " - " + customer.customer_name;
			vm.$refs.payment_mode_id.focus();
		},

		addSession() {
			let vm = this;
			vm.syncDateTimeFromInput();
			vm.session.tournament_event_id = vm.selectedTournamentEventId || "";
			if (!vm.session.tournament_event_id) {
				vm.errors = Object.assign({}, vm.errors, {
					tournament_event_id: ["Please select event"],
				});
				vm.$store.dispatch("error", "Please select event");
				return;
			}
			let loader = vm.$loading.show();
			let uri = {
				uri: "addSession",
				data: Object.assign({}, vm.session, { denominations: [] }),
			};
			vm.$store
				.dispatch("post", uri)
				.then(function (response) {
					loader.hide();
					const sessionData = response?.data?.data || {};
					const slip = vm.buildTournamentSlip({
						receiptNo: sessionData.receipt_no || "",
					});
					vm.$store.dispatch("success", "Tournament entry saved successfully");
					vm.printThermalReceipt(slip);
					vm.discard();
				})
				.catch(function (error) {
					loader.hide();
					vm.errors = error.response.data.errors || error.response.data;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},

		buildTournamentSlip(opts) {
			let vm = this;
			opts = opts || {};
			const tableRow = vm.tables.find(
				(t) => String(t.table_id) === String(vm.session.table_id)
			);
			const selectedEvent = vm.selectedEvent || {};
			const paymentMode = (vm.payment_modes || []).find(
				(pm) => String(pm.payment_mode_id) === String(vm.session.payment_mode_id)
			);
			const user = vm.$store.getters.user || {};
			const b = user.branch || {};
			const amt = vm.session.amount;
			const amountStr =
				amt === "" || amt === null || amt === undefined
					? ""
					: Number(amt).toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					});
			const branchPhoneParts = [b.mobile_no && "Mob: " + b.mobile_no, b.email].filter(Boolean);
			const sessionMoment = vm.datetime
				? moment(vm.datetime, "YYYY-MM-DDTHH:mm")
				: moment(
					[vm.session.session_date, vm.session.session_time].filter(Boolean).join(" "),
					["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"]
				);
			return {
				title: "TOURNAMENT",
				logoUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwAAAC2AQAAAABLjI1JAAAFGUlEQVR42u2Yv47bRhDGf7siTiwOFpEmVxgR07kJ4DIBjBOB5CkCBBDyBulSGNbaLtz6Ee4RXKYK9oAAdpkqVYAwgYtDqj3bAfbOFCcFKYqkuJRsxykSb6G/H3dmv535ZnaVcMC41Bw0/pOwB1ylOz+qPm9XMy+Tci9vz2KUZDvTSXeUzEXMtPer6892TQrZ9b4lXJBBKmaPUYuIOBZ7jKKAmHyPUauBaC9vaupB9MyN8ibEgMKPG7XVm+k714XJPRIAiosxWFmQAmTlzTFYkZEBpJRjMO8b0sdgDgxAUr83o8tk3tBlfhuZzaIAiOkz0tlilkpERArSWXjrhbz57sNGy+2vSRE26mFah3qiOkY7K/UdasbiLW5i0wRh7rCszwdiathosgm47jM6OEHYqAk62jdai1E2Mls3xvxhMliEYOUbi2rae0j3zGRvKtESgvnDfIt3FGyf0eQw39xhsGREBtdRs77LhEnxr5RdHVaX92dUDWTsAMyEKW7DrGmgrpe1upfNtvmYjC0h32asDQjXzEItksBiNlIo/TZyfSh6O43FIh6GSVdHSx/wbeJgUosqFNNh36TsJHYw66XtjfLvKlw/tf8Iq+UfQbkZUnKhgMgGqkzJDJSIFOJhRoCQpq0odqWzY/Ta1O0UcCMEEyRDuFKf4uFmCKbgbDMX3CIeKeJQl958vHMgXw5VhnalB1hZpuKgCeThNg87VDF3Ydm+zapY+/UgDbHfDinmsNTk/4AiZc1EdqwntwCTYiPOizHe1gaeH6CWdmAFHVi6WaXd4VkPFO9yfKXJWym5GYUN9lPt0nbV0XhVvltpi8Jev78KqDr6dhI0ugx+6Rx5OoxI8MjTdmcSXnb7r+PRg0Azbo8cx1rOychx7Kj5NB0LpC1zJ6NnwMtksGvtGl3rxjcZi4Nt1dIjMBVsDXUo/JMRWLbTnA/C0pBodgm5TA7ahTjQVhHarKPRzQrw0c8sE9CTHmw5mAlvt/VwDLfB9jJhx+gE9QR+2Ll16IuDuZHD035U7sC+cxZyv9wDuwHgpmbfBZFomPq9F0Q7Pdmwvg3fruze1bihI5L6cOn3AbYPdh7BeUKa8vi+WSv1xWNePKui6X78u9KcZyAyd6ty8VByEbd4JIbTqRRzkZcixSIvULKYOc16GdvX9hsUkH3Nl6W6i07BwSub6LlbZwbNX+aIa04MEJuPQVGiMijhJTHEmAzNCxSvNn1/ZMDIpld6TgRRQYrmedVgt051qanF5llDhMaCsWjXHE2ySpV+rtVOVy8xYDY6qRwQW4BbRpK61iVoTPV8K+mivCufBjRJpXm1anwPtQPGlmmrmg1sjQf4ql3ade8Gp0AqP6XpMKO8hvW7xLsAD+onvb4YipASToFy0+85dQ267JWydQRzQNUU/Z6yGphtUkBiNouGec4yQw8JXGyh2HQRCUkejl7ZauaxQwPnH7W7KVVR8FpsVBc57dE4Pk/6Z1MH0ZYmVUtt2ut1lQdH2hBs0Aq02hbQeMNvjLR2SOM7FwLV8k6bkmrAk1WBhIHH1Z5VITkHVwffhqU71b5XQSPp9lARG4+A3CVC8wlwilTgsinhtrpGqf3QzIA5pQUPhQVbLyTiDiWUJTGa2JR5YtaA8+YKMDlRdQ/6GQUUgkVzdAbHZ38inGF/4cc1Bo04cMfG33MXsXkCIg/zVclcYGKYF3AyEVl5JflEZo9yKI5XDpFiIXIpg8OtRESeivv/lLa/AQ6l0az9rcIFAAAAAElFTkSuQmCC",
				branchName: b.branch_name || b.description || "",
				unitLine: "Unit of DM Gaming Private Limited",
				branchCode: b.branch_code || "",
				branchAddress: b.address || "",
				branchGst: b.gst_no || "",
				branchContact: branchPhoneParts.join(" · "),
				cashier: user.name || user.username || user.email,
				playerId: vm.session.customer_no || "",
				playerName: vm.session.customer_real_name || vm.session.customer_name || "",
				eventTitle: selectedEvent.title || "Tournament Event",
				eventDate: selectedEvent.start_date ? moment(selectedEvent.start_date).format("DD-MMM-YYYY") : "",
				eventTime: (selectedEvent.start_time || "")
					? moment((selectedEvent.start_time || "").toString().slice(0, 8), ["HH:mm:ss", "HH:mm"]).format("hh:mm A")
					: "",
				buyInAmount: selectedEvent.amount != null ? Number(selectedEvent.amount).toFixed(2) : "",
				houseFee: selectedEvent.house_fee != null ? Number(selectedEvent.house_fee).toFixed(2) : "",
				amount: amountStr,
				paymentMode: paymentMode ? paymentMode.payment_mode : "",
				referenceNo: vm.session.reference_no || "",
				receiptNo: opts.receiptNo || "",
				dataFields: Array.isArray(selectedEvent.data_fields) ? selectedEvent.data_fields.filter(Boolean) : [],
				note: vm.session.note,
				tableName: tableRow ? tableRow.table : String(vm.session.table_id || ""),
				seatNo: vm.session.seat_no,
				dateDisplay: sessionMoment.isValid() ? sessionMoment.format("DD-MMM-YYYY") : "",
				timeDisplay: sessionMoment.isValid() ? sessionMoment.format("hh:mm A") : "",
				datetime: sessionMoment.isValid()
					? sessionMoment.format("YYYY-MM-DD HH:mm:ss")
					: [vm.session.session_date, vm.session.session_time].filter(Boolean).join(" "),
				sessionLogId: vm.session.session_log_id,
			};
		},

		printThermalReceipt(slip) {
			const esc = (s) => {
				if (s == null || s === "") {
					return "";
				}
				return String(s)
					.replace(/&/g, "&amp;")
					.replace(/</g, "&lt;")
					.replace(/>/g, "&gt;")
					.replace(/"/g, "&quot;");
			};
			const line = (label, value) =>
				`<div class="row"><span class="label">${esc(label)}</span><span class="value">${esc(value)}</span></div>`;
			const copySection = (copyLabel) => `
<div class="receipt">
  <div class="logo-block">
    <img src="${esc(slip.logoUrl)}" width="150" alt="Golden Aces" style="display:block; margin:0 auto;">
  </div>
  <div class="center small">${esc(slip.unitLine || "")}</div>
  <div class="center brand">${esc(slip.branchName || "")}</div>
  ${slip.branchAddress ? `<div class="center small">${esc(slip.branchAddress)}</div>` : ""}
  ${slip.branchGst ? `<div class="center small">GSTIN: ${esc(slip.branchGst)}</div>` : ""}
  ${slip.branchContact ? `<div class="center small">${esc(slip.branchContact)}</div>` : ""}
  <div class="sep-double"></div>
  <div class="center title">TOURNAMENT BUY-IN</div>
  <div class="sep-double"></div>

  ${line("Receipt #", slip.receiptNo || "-")}
  ${line("Date", slip.dateDisplay || "-")}
  ${line("Time", slip.timeDisplay || "-")}
  ${line("Session", slip.sessionLogId || "-")}
  <div class="sep-dash"></div>

  <div class="block-title">TOURNAMENT</div>
  <div>${esc(slip.eventTitle || "-")}</div>
  ${(slip.eventDate || slip.eventTime) ? `<div class="small">${esc([slip.eventDate, slip.eventTime].filter(Boolean).join(" "))}</div>` : ""}
  ${(slip.dataFields || []).length > 0
					? `<div class="small" style="margin-top:4px;">${(slip.dataFields || [])
						.map((f) => esc(f))
						.join(", ")}</div>`
					: ""
				}
  <div class="sep-dash"></div>

  ${line("Player ID", slip.playerId || "-")}
  ${line("Player", slip.playerName || "-")}
  ${line("Cashier", slip.cashier || "-")}
  <div class="sep-dash"></div>

  <div class="block-title">AMOUNT PAID</div>
  <table class="breakup" style="width:100%; border-collapse:collapse;">
    <tr><td class="lbl">Tournament Buy-in</td><td class="amt">${esc(slip.buyInAmount || slip.amount || "0.00")}</td></tr>
    <tr><td class="lbl">House Fee</td><td class="amt">${esc(slip.houseFee || "0.00")}</td></tr>
    <tr class="total"><td class="lbl">TOTAL (Rs.)</td><td class="amt">${esc(slip.amount || "0.00")}</td></tr>
  </table>
  <div class="sep-dash"></div>

  ${line("Mode", slip.paymentMode || "-")}
  ${line("Ref No", slip.referenceNo || "-")}

  <div class="seat-box">
    <div class="half">
      <div class="lbl">TABLE</div>
      <div class="num">${esc(slip.tableName || "-")}</div>
    </div>
    <div class="half">
      <div class="lbl">SEAT</div>
      <div class="num">${esc(slip.seatNo != null && slip.seatNo !== "" ? String(slip.seatNo) : "-")}</div>
    </div>
  </div>

  ${slip.note ? `<div class="note">${esc(slip.note)}</div>` : ""}
  <div class="disclaimer">Disclaimer: Golden Aces is an offline card room chain. Only skill-based games are allowed.</div>
  <div class="disclaimer">Age 18+ Allowed | Habit Forming | Play Responsibly</div>
  <div class="copy-tag">${esc(copyLabel)}</div>
</div>`;

			const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(
				slip.title
			)}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    font-family: "Courier New", "Consolas", monospace;
    font-size: 12px;
    line-height: 1.35;
    color: #000;
  }
  .receipt { width: 72mm; padding: 4mm 2mm; margin: 0 auto; }
  .logo-block { text-align: center; margin-bottom: 2px; }
  .center { text-align: center; }
  .brand { font-size: 14px; font-weight: 700; letter-spacing: 0.5px; }
  .title { font-size: 14px; font-weight: 800; letter-spacing: 1px; }
  .small { font-size: 10px; }
  .block-title { font-size: 11px; font-weight: 700; }
  .sep-double { border-top: 3px double #000; margin: 4px 0; }
  .sep-dash { border-top: 1px dashed #000; margin: 4px 0; }
  .row { display: flex; justify-content: space-between; gap: 6px; margin: 1px 0; }
  .row .label { white-space: nowrap; }
  .row .value { text-align: right; }
  .breakup td { padding: 1px 0; font-size: 12px; }
  .breakup .lbl { width: 60%; }
  .breakup .amt { width: 40%; text-align: right; }
  .breakup .total td { border-top: 1px solid #000; font-weight: 700; padding-top: 2px; }
  .seat-box { display: flex; width: 100%; margin: 8px 0; border: 2px solid #000; }
  .seat-box .half { flex: 1; text-align: center; padding: 6px 2px 8px; }
  .seat-box .half + .half { border-left: 2px solid #000; }
  .seat-box .lbl { font-size: 10px; font-weight: 700; letter-spacing: 2px; margin-bottom: 2px; }
  .seat-box .num { font-size: 26px; font-weight: 900; line-height: 1; font-family: Arial, sans-serif; }
  .note { font-size: 10px; white-space: pre-wrap; word-break: break-word; margin-top: 4px; }
  .disclaimer { font-size: 9px; line-height: 1.3; margin-top: 4px; }
  .copy-tag { text-align: center; font-weight: 900; letter-spacing: 3px; font-size: 14px; background: #000; color: #fff; padding: 6px 4px; margin-top: 8px; }
  .page-break { page-break-after: always; }
</style></head><body>
  ${copySection("PLAYER COPY")}
  <div class="page-break"></div>
  ${copySection("DEALER COPY")}
</body></html>`;

			const iframe = document.createElement("iframe");
			iframe.setAttribute("aria-hidden", "true");
			iframe.style.cssText =
				"position:fixed;width:0;height:0;border:0;right:0;bottom:0;opacity:0;pointer-events:none;";
			document.body.appendChild(iframe);
			const doc = iframe.contentWindow.document;
			doc.open();
			doc.write(html);
			doc.close();
			const win = iframe.contentWindow;
			const runPrint = function () {
				try {
					win.focus();
					win.print();
				} finally {
					setTimeout(function () {
						if (iframe.parentNode) {
							iframe.parentNode.removeChild(iframe);
						}
					}, 500);
				}
			};
			setTimeout(runPrint, 150);
		},

		discard() {
			let vm = this;
			vm.session.session_log_id = vm.session_log_id || "";
			vm.session.customer_id = "";
			vm.session.customer_name = "";
			vm.session.customer_no = "";
			vm.session.customer_real_name = "";
			vm.session.tournament_event_id = vm.selectedTournamentEventId || "";
			vm.session.amount = "";
			vm.session.payment_mode_id = "";
			vm.session.reference_no = "";
			vm.session.note = "";
			vm.datetime = moment().format("YYYY-MM-DDTHH:mm");
			vm.syncDateTimeFromInput();
			vm.errors = [];
			vm.applyNextTournamentSlot();
			vm.loadTournamentEvents();
			vm.$nextTick(() => {
				if (vm.$refs.customer_id) {
					vm.$refs.customer_id.focus();
				}
			});
		},
	},
};
</script>
